import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMatchRoute, useNavigate } from '@tanstack/react-router';
import { markTutorial } from '@/shared/utils/tutorials';
import { isDemoAccount } from '@/modules/demo-account';
import { track } from '@/shared/utils/track';
import { useProfile } from '@/routes/Providers/ProfileProvider';
import {
  OnboardingContext,
  type OnboardingContextValue,
  type OnboardingPath,
  type OnboardingStep,
  type RouteKey,
  type StepDef,
} from './context';
import WelcomeModal from './WelcomeModal';
import FinishModal from './FinishModal';
import OnboardingCoachmarks from './OnboardingCoachmarks';

// Defined in ./context, not here, so the sibling files never import the
// provider back (an import cycle).
export { useOnboarding } from './context';

// The onboarding e2e spec opts back in via a window global from
// addInitScript — a window flag, NOT localStorage, because the KEYS.ts
// reset sweeper wipes unknown storage keys on a fresh profile's first boot.
const enabledInEnv =
  import.meta.env.VITE_E2E_PRIVATE_KEY == null ||
  import.meta.env.VITE_E2E_PRIVATE_KEY === '' ||
  window.__E2E_ONBOARDING__ === true;

export const PATH_STEPS: Record<OnboardingPath, StepDef[]> = {
  trader: [
    {
      id: 'nav-markets',
      selector: '[data-onboarding="nav-markets"]',
      route: 'markets',
      title: 'Post predictions',
      body: 'Agree or disagree with signals from leading crypto voices and Santiment analysts. Every prediction builds your track record.',
    },
    {
      id: 'profile-stats',
      selector: '[data-onboarding="profile-stats"]',
      route: 'profile',
      title: 'Prove your edge',
      body: 'The more accurate you are over time, the higher you climb. A strong track record puts you in the running for funding.',
    },
    {
      id: 'nav-community',
      selector: '[data-onboarding="nav-community"]',
      route: null,
      title: 'Get backed by the community',
      body: 'Backers put SAN behind the traders they believe in. The more conviction the community shows in you, the closer you get to funded capital.',
    },
    {
      id: 'nav-arena',
      selector: '[data-onboarding="nav-arena"]',
      route: null,
      title: 'Trade funded capital, keep half',
      body: "Once funded, your calls are executed with Santiment's capital on Hyperliquid. You keep 50% of the profits you generate, never depositing or risking your own money.",
    },
    {
      id: 'profile-pill',
      selector: '[data-onboarding="profile-pill"]',
      route: null,
      title: 'Scale as you perform',
      body: 'Keep performing to earn greater backing, which grows your allocation. More capital, same share. Your track record becomes a career asset you own.',
    },
  ],
  staker: [
    {
      id: 'nav-community',
      selector: '[data-onboarding="nav-community"]',
      route: null,
      title: 'Find talent worth backing',
      body: "Browse the leaderboard. Look at who's accurate, who's consistent, who's on the rise. Your edge is spotting real skill before the crowd does.",
    },
    {
      id: 'stake-button',
      selector: '[data-onboarding="stake-button"]',
      route: 'community',
      title: 'Back them with SAN',
      body: 'Connect your wallet, swap your crypto for SAN tokens, pick a trader, and back them with the SAN. Backing is a sign of belief, your SAN never touch any trades.',
    },
    {
      id: 'nav-arena',
      selector: '[data-onboarding="nav-arena"]',
      route: null,
      title: 'Your conviction moves capital',
      body: "Backing isn't passive. The traders the community backs most are the ones who get funded. Your backing helps decide who trades real capital.",
    },
    {
      id: 'san-balance',
      selector: '[data-onboarding="san-balance"]',
      route: 'profile',
      title: 'Earn when your pick wins',
      body: "When a trader you've backed is funded and profitable, you share in the profits, paid out weekly. Spot winners early and it pays off more.",
    },
    // Deliberately anchored to the header profile pill, not the Stake-SAN
    // button.
    {
      id: 'profile-pill',
      selector: '[data-onboarding="profile-pill"]',
      route: null,
      title: 'Curation is a skill you build',
      body: "Your backing choices build their own track record. Consistently pick winners and you become a recognized talent scout, a reputation that's yours to keep.",
    },
  ],
};

export default function OnboardingProvider({
  children,
}: {
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const { profile, updateProfile } = useProfile();
  const enabled = !!profile && enabledInEnv;

  const [welcomeOpen, setWelcomeOpen] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  // Set by the Arena Setup checklist's "Complete" button (reopenWelcome) —
  // an explicit re-entry must beat the tutorials suppression below.
  const [forcedWelcome, setForcedWelcome] = useState(false);
  // Known as soon as `profile` exists (tutorials ride on the auth payload),
  // and `enabled` requires profile — no pre-suppression flash.
  const suppressed =
    profile?.tutorials?.onboarding_completed === true ||
    profile?.tutorials?.onboarding_skipped === true ||
    (profile != null && isDemoAccount(profile));
  const [active, setActive] = useState<{
    path: OnboardingPath;
    index: number;
  } | null>(null);
  const [finish, setFinish] = useState<OnboardingPath | null>(null);

  // The routed steps' pages. Without a profile the tour never runs, so the
  // profile step reads as "not there" rather than routing to /user/undefined.
  const username = profile?.username;
  // eslint-disable-next-line no-restricted-syntax -- step, the effect dependency below, derives from it
  const isOnRoute = useCallback(
    (key: RouteKey): boolean =>
      key === 'markets' ? matchRoute({ to: '/markets' }) !== false
      : key === 'community' ? matchRoute({ to: '/community' }) !== false
      : username !== undefined &&
        matchRoute({ to: '/user/$username', params: { username } }) !== false,
    [matchRoute, username],
  );
  const goToRoute = (key: RouteKey) => {
    if (key === 'markets') void navigate({ to: '/markets' });
    else if (key === 'community') void navigate({ to: '/community' });
    else if (username !== undefined) {
      void navigate({ to: '/user/$username', params: { username } });
    }
  };

  const startPath = (path: OnboardingPath) => {
    setWelcomeOpen(false);
    setFinish(null);
    setActive({ path, index: 0 });
    const route = PATH_STEPS[path][0]?.route;
    // Already there (the common case: welcome opens over /markets) —
    // navigating would push a duplicate history entry.
    if (route != null && !isOnRoute(route)) goToRoute(route);
  };
  const chooseTrader = () => {
    track('onboarding_path_chosen', { path: 'trader' });
    startPath('trader');
  };
  const chooseStaker = () => {
    track('onboarding_path_chosen', { path: 'staker' });
    startPath('staker');
  };

  const dismiss = () => {
    // Not when dismissing from the finish card ("Get started"): reaching the
    // finish already latched `onboarding_completed`, and
    // skipped-without-completing is what the Arena Setup checklist uses to
    // keep offering its "Complete" re-entry.
    if (finish == null) {
      track('onboarding_skipped', {
        path: active?.path,
        step_index: active?.index,
      });
      void markTutorial('onboarding_skipped', profile?.tutorials)
        .then(() => updateProfile())
        .catch(() => {
          // The latch is on the server; the next profile refresh carries it.
        });
    }
    setWelcomeOpen(false);
    setActive(null);
    setFinish(null);
    setDismissed(true);
  };

  const next = () => {
    if (!active) return;
    const steps = PATH_STEPS[active.path];
    const nextIndex = active.index + 1;
    const nextStep = steps.at(nextIndex);
    if (!nextStep) {
      setActive(null);
      setFinish(active.path);
      track('onboarding_finished', { path: active.path });
      // ONE finished path = onboarding completed (the finish card says
      // "Welcome on board!" — the second tour is optional cross-promo).
      // updateProfile refreshes profile.tutorials so the checklist reflects
      // it without a reload.
      void markTutorial('onboarding_completed', profile?.tutorials)
        .then(() => updateProfile())
        .catch(() => {
          // The latch is on the server; the next profile refresh carries it.
        });
      return;
    }
    setActive({ path: active.path, index: nextIndex });
    const route = nextStep.route;
    if (route != null && !isOnRoute(route)) goToRoute(route);
  };

  // eslint-disable-next-line no-restricted-syntax -- effect dependency in the Community page's tour-termination effect
  const pathUnavailable = useCallback(() => {
    setActive(null);
    setDismissed(true);
  }, []);

  const reopenWelcome = () => {
    setActive(null);
    setFinish(null);
    setDismissed(false);
    setForcedWelcome(true);
    setWelcomeOpen(true);
  };

  // GDPR hold: a brand-new user must consent first — the GDPR modal and the
  // welcome are both Modals, and the later-mounted welcome would sit on top.
  const welcomeVisible =
    enabled &&
    welcomeOpen &&
    !dismissed &&
    (forcedWelcome || !suppressed) &&
    profile.GDPR != null &&
    profile.GDPR !== '';

  // welcome_shown fires once per appearance; step_viewed once per
  // (path, index) — the render-gate pause/resume must not re-fire it.
  const welcomeShownRef = useRef(false);
  useEffect(() => {
    if (!welcomeVisible) {
      welcomeShownRef.current = false;
      return;
    }
    if (welcomeShownRef.current) return;
    welcomeShownRef.current = true;
    track('onboarding_welcome_shown', {
      source: forcedWelcome ? 'checklist' : 'auto',
    });
  }, [welcomeVisible, forcedWelcome]);

  // Render gate: a routed step is only "on screen" while the user is on its
  // page — navigating away mid-step PAUSES the tour (nothing renders, page
  // fully usable) and it resumes on return. Route-less steps anchor global
  // nav and render anywhere.
  // eslint-disable-next-line no-restricted-syntax -- effect dependency in the step_viewed effect below and in the Community page's tour-termination effect
  const step = useMemo<OnboardingStep | null>(() => {
    if (!enabled || !active || dismissed) return null;
    const steps = PATH_STEPS[active.path];
    const def = steps[active.index];
    if (!def) return null;
    if (def.route != null && !isOnRoute(def.route)) return null;
    return {
      ...def,
      index: active.index,
      count: steps.length,
      path: active.path,
    };
  }, [enabled, active, dismissed, isOnRoute]);

  const lastViewedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!step) return;
    const key = `${step.path}-${step.index}`;
    if (lastViewedRef.current === key) return;
    lastViewedRef.current = key;
    track('onboarding_step_viewed', {
      path: step.path,
      step_id: step.id,
      index: step.index,
    });
  }, [step]);

  const value: OnboardingContextValue = {
    step,
    next,
    dismiss,
    pathUnavailable,
    chooseTrader,
    chooseStaker,
    reopenWelcome,
  };

  return (
    <OnboardingContext value={value}>
      {children}
      {welcomeVisible && <WelcomeModal />}
      {enabled && finish != null && !dismissed && (
        <FinishModal
          path={finish}
          onSeeOther={() => {
            track('onboarding_finish_action', { action: 'see_other_tour' });
            startPath(finish === 'trader' ? 'staker' : 'trader');
          }}
          onClose={() => {
            track('onboarding_finish_action', { action: 'get_started' });
            dismiss();
          }}
        />
      )}
      <OnboardingCoachmarks />
    </OnboardingContext>
  );
}
